import dayjs from "dayjs";

export const getTravellerDetailEndpoint = (travellerId) =>
  travellerId ? `issuance/policies/traveller/${travellerId}/` : null;

const CONDITION_MAP = {
  hypertension: "High blood pressure (Hypertension)",
  heart_disease: "Heart disease",
  asthma_copd: "Asthma / COPD",
  thyroid: "Thyroid disorders",
  cancer: "Cancer / history of cancer",
  gi_disease: "Chronic gastrointestinal diseases",
  obesity: "Obesity",
  mental_health: "Chronic mental-health conditions",
  none: "None of the above",
  none_of_the_above: "None of the above",
};

const val = (src, keys) => keys.map((key) => src[key]).find((item) => item !== undefined);

const isOn = (value) => value === true || value === "true" || value === "Yes";

const tri = (src, keys) => {
  const items = keys.map((key) => src[key]).filter((item) => item !== undefined && item !== null && item !== "");
  if (!items.length) return undefined;
  return items.some(isOn);
};

const splitList = (value) =>
  (Array.isArray(value) ? value : String(value || "").split(/[,;]/))
    .map((item) => String(item).trim())
    .filter((item) => item && item.toUpperCase() !== "NIL");

const detail = (src, keys) =>
  keys.map((key) => src[key]).find((item) => item != null && String(item).trim()) || "";

const yn = (label, yes, extra) => (yes === undefined ? null : { label, yes, detail: extra });

const text = (label, value) => (value ? { label, value } : null);

const declaredLabels = (src) => {
  const pre = src.booking_pre_existing_conditions;
  if (pre?.labels?.length) return pre.labels;
  if (pre?.conditions?.length) return pre.conditions.map((item) => item.label).filter(Boolean);
  return splitList(src.medical_allergy).map((key) => {
    const slug = String(key).trim().toLowerCase().replace(/\s+/g, "_");
    return CONDITION_MAP[slug] || key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  });
};

export const getMedicalHistoryView = (traveller) => {
  const nested = traveller?.medical_history;
  const src = {
    blood_group: traveller?.blood_group,
    medical_allergy: traveller?.medical_allergy,
    medicinal_allergy: traveller?.medicinal_allergy,
    ...(nested && typeof nested === "object" && !Array.isArray(nested) ? nested : {}),
  };
  const urlRaw = traveller?.medical_history_url || src.url || src.medical_history_url;
  const url = typeof urlRaw === "string" && /^https?:\/\//i.test(urlRaw.trim()) ? urlRaw : "";

  const sys = val(src, ["blood_pressure_systolic", "bp_systolic"]);
  const dia = val(src, ["blood_pressure_diastolic", "bp_diastolic"]);
  const height = val(src, ["height_cm", "height"]);
  const weight = val(src, ["weight_kg", "weight"]);

  const general = [
    text("Height", height != null && height !== "" ? `${height} cm` : null),
    text("Weight", weight != null && weight !== "" ? `${weight} kg` : null),
    text("Blood Group", val(src, ["blood_group"])),
    text("BP", sys != null && dia != null ? `${sys}/${dia} mmHg` : null),
    yn("History of BP", tri(src, ["history_of_bp"]), detail(src, ["history_of_bp_details"])),
    yn("Smoker", tri(src, ["smoker", "is_smoker"])),
    yn("Alcohol", tri(src, ["alcohol", "consumes_alcohol"])),
  ].filter(Boolean);

  const declared = declaredLabels(src);
  const field = (label, flags, extra) => yn(label, tri(src, flags), detail(src, extra));

  const conditions = [
    field("Heart disease / Hypertension", ["heart_disease", "hypertension"], ["heart_disease_details"]),
    field("Asthma / Respiratory", ["asthma", "asthma_copd", "respiratory"], ["asthma_details"]),
    field("Diabetes", ["diabetes"], ["diabetes_details"]),
    field("Epilepsy / Seizures", ["epilepsy", "seizures"], ["epilepsy_details"]),
    field("Altitude sickness", ["altitude_sickness"], ["altitude_sickness_details"]),
    field("Bone / Joint problems", ["bone_joint", "bone_joint_problems"], ["bone_joint_details"]),
    field("Recent surgeries", ["recent_surgeries"], ["recent_surgeries_details"]),
    field("Chronic illness", ["chronic_illness"], ["chronic_illness_details"]),
    field("Allergies", ["allergies"], ["allergies_details"]),
  ].filter(Boolean);

  const vaccines = [
    { key: "tetanus_vaccine", label: "Tetanus" },
    { key: "influenza_vaccine", label: "Influenza" },
    { key: "typhoid_vaccine", label: "Typhoid" },
  ]
    .filter((item) => src[item.key] !== undefined && src[item.key] !== null)
    .map((item) => ({ label: item.label, on: isOn(src[item.key]) }));

  const current = [
    field("On medication", ["on_medication", "taking_medication"], ["medication_details"]),
    field("Undergoing treatment", ["undergoing_treatment"], ["treatment_details"]),
    vaccines.length ? { label: "Vaccinations", vaccines } : null,
  ].filter(Boolean);

  const trekked = tri(src, ["previous_trekking"]);
  const fitness = [
    yn("Previous trekking", trekked),
    trekked ? text("Last activity", val(src, ["last_activity"])) : null,
    field("Difficulty in previous treks", ["trek_difficulty", "difficulty_in_previous_treks"], ["trek_difficulty_details"]),
  ].filter(Boolean);

  const signedOn = val(src, ["signed_on", "declaration_date", "signature_date"]);
  const declaration = [
    text("Signed by", val(src, ["participant_signature", "signed_by", "signature"])),
    text("Date", signedOn ? dayjs(signedOn).format("MMM D, YYYY") : null),
  ].filter(Boolean);

  const hasData = [general, declared, conditions, current, fitness, declaration].some((list) => list.length);
  return { general, declared, conditions, current, fitness, declaration, url, hasData };
};
